/** Boulders and diamonds: standing and falling (docs/spec/02 §3), with slime and the magic wall. */

import { E, EXPLODES_ON_HIT, ROUNDED } from '../elements';
import { at, isSpace, move, put, store } from '../grid';
import { RULES } from '../tuning';
import type { CaveState } from '../types';
import { DIR_DOWN, DIR_LEFT, DIR_RIGHT } from '../types';
import { explodeHit } from './explode';

/** Roll off a rounded thing below: left first, then right. True if it moved. */
function roll(s: CaveState, x: number, y: number, falling: number): boolean {
  if (isSpace(s, x - 1, y) && isSpace(s, x - 1, y + 1)) {
    move(s, x, y, DIR_LEFT, falling);
    return true;
  }
  if (isSpace(s, x + 1, y) && isSpace(s, x + 1, y + 1)) {
    move(s, x, y, DIR_RIGHT, falling);
    return true;
  }
  return false;
}

/** Resting on slime with space beneath it: sometimes drop through (02 §7.3). */
function seep(s: CaveState, x: number, y: number, falling: number): boolean {
  if (!isSpace(s, x, y + 2) || !s.rng.oneIn(RULES.slimeOneIn)) return false;
  put(s, x, y, E.SPACE);
  store(s, x, y + 2, falling);
  s.events.push({ type: 'slime', x, y: y + 1 });
  return true;
}

/** A falling object meets a running (or dormant) magic wall: it goes in, and maybe out changed. */
function enchant(s: CaveState, x: number, y: number, isBoulder: boolean): void {
  if (s.magic.phase === 'dormant') {
    s.magic.phase = 'active';
    s.magic.msLeft = s.params.magicWallTime * 1000;
  }
  put(s, x, y, E.SPACE);
  if (s.magic.phase === 'active' && isSpace(s, x, y + 2)) {
    store(s, x, y + 2, isBoulder ? E.DIAMOND_FALLING : E.BOULDER_FALLING);
  }
  s.events.push({ type: 'magicConvert', x, y: y + 1 });
}

export function standing(s: CaveState, x: number, y: number, isBoulder: boolean): void {
  const falling = isBoulder ? E.BOULDER_FALLING : E.DIAMOND_FALLING;
  const below = at(s, x, y + 1);
  if (below === E.SPACE) move(s, x, y, DIR_DOWN, falling);
  else if (below === E.SLIME) seep(s, x, y, falling);
  else if (ROUNDED[below] === 1) roll(s, x, y, falling);
}

export function falling(s: CaveState, x: number, y: number, isBoulder: boolean): void {
  const self = isBoulder ? E.BOULDER_FALLING : E.DIAMOND_FALLING;
  const below = at(s, x, y + 1);
  if (below === E.SPACE) {
    move(s, x, y, DIR_DOWN, self);
    return;
  }
  if (EXPLODES_ON_HIT[below] === 1) {
    explodeHit(s, x, y + 1, below);
    return;
  }
  if (below === E.MAGIC_WALL && s.magic.phase !== 'expired') {
    enchant(s, x, y, isBoulder);
    return;
  }
  if (below === E.SLIME && seep(s, x, y, self)) return;
  if (ROUNDED[below] === 1 && roll(s, x, y, self)) return;
  put(s, x, y, isBoulder ? E.BOULDER : E.DIAMOND);
  s.events.push({ type: 'land', x, y, what: isBoulder ? 'boulder' : 'diamond' });
}
